import type { ModuleFeature, SubscriptionChange } from "@cafe/shared";
import { Badge, Button, formatMoney } from "@cafe/ui";
import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight, BarChart3, Bike, Boxes, CalendarCheck, CalendarHeart, Check, Factory, Gift, HandCoins, Lock, Megaphone, MessageSquareText, QrCode, Send, ShieldAlert,
  Sparkles, Timer, Truck, Wallet,
} from "lucide-react";
import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { ChangeModal } from "@/features/billing/change-modal";
import { http } from "@/shared/api/client";
import { useCan } from "@/shared/hooks/session";
import { featureLabel } from "@/shared/lib/options";
import type { ru } from "@/shared/i18n/ru";
import "./feature-promo.css";

type PromoKey = keyof typeof ru.promo.items;
type Catalog = {
  currency: string; currentPlan: string;
  plans: { code: string; name: string; features: string[] }[];
  addons: { code: string; mode: string; active: boolean; prices: { period: string; amount: number }[] }[];
};

/** Функция тарифа → описание (`promo.items.*`) и иконка; без описания — общий текст. */
const PROMO: Partial<Record<ModuleFeature, { key: PromoKey; icon: LucideIcon }>> = {
  "addon.inventory_pro": { key: "inventory_pro", icon: Boxes },
  "addon.production": { key: "production", icon: Factory },
  "addon.procurement": { key: "procurement", icon: Truck },
  "addon.finance": { key: "finance", icon: Wallet },
  "addon.fraud": { key: "fraud", icon: ShieldAlert },
  "addon.sms": { key: "sms", icon: MessageSquareText },
  "addon.services": { key: "services", icon: Timer },
  "addon.banquets": { key: "banquets", icon: CalendarHeart },
  "addon.promotions_pro": { key: "promotions_pro", icon: Gift },
  "addon.delivery": { key: "delivery", icon: Bike },
  "addon.reservations": { key: "reservations", icon: CalendarCheck },
  "addon.analytics_pro": { key: "analytics_pro", icon: BarChart3 },
  "addon.telegram": { key: "telegram", icon: Send },
  "addon.qr_ordering": { key: "qr_ordering", icon: QrCode },
  "core.debts": { key: "debts", icon: HandCoins },
};
const BENEFIT_ICONS = [Sparkles, BarChart3, Megaphone, Check] as const;
const BENEFITS = ["b1", "b2", "b3", "b4"] as const;
const INSIDE = ["f1", "f2", "f3", "f4", "f5", "f6"] as const;

/**
 * Раздел не входит в тариф (ТЗ 12 §2 Feature gating): витрина дополнения — что это, зачем, что внутри — и «Подключить»
 * (предпросмотр суммы и оформление прямо здесь). Функция только в другом тарифе — «Сменить тариф».
 */
export function FeaturePromo({ feature }: { feature: ModuleFeature }) {
  const { t, i18n } = useTranslation();
  const can = useCan();
  const promo = PROMO[feature];
  const Icon = promo?.icon ?? Sparkles;
  const name = featureLabel(feature);
  const catalog = useQuery({ queryKey: ["subscription", "catalog"], queryFn: () => http.get<Catalog>("/subscription/catalog"), enabled: can("subscription.view"), staleTime: 60_000 });
  const [change, setChange] = useState<{ change: SubscriptionChange; title: string } | null>(null);
  const addon = catalog.data?.addons.find((a) => a.code === feature);
  const price = addon?.prices.find((p) => p.period === "month")?.amount;
  const plans = (catalog.data?.plans ?? []).filter((p) => p.features.includes(feature) && p.code !== catalog.data?.currentPlan).map((p) => p.name);
  const purchasable = !!addon && addon.mode === "purchasable" && !addon.active;
  const manage = can("subscription.manage");
  const has = (k: string) => i18n.exists(`promo.items.${promo?.key}.${k}`);
  const tx = (k: string) => (promo ? t(`promo.items.${promo.key}.${k}` as "promo.items.inventory_pro.title") : "");
  const title = promo ? tx("title") : name;
  const tagline = promo ? tx("tagline") : t("promo.genericTagline", { name });
  // Плашки вокруг иконки — короткие заголовки преимуществ
  const chips = BENEFITS.filter((b) => has(`${b}.t`)).slice(0, 3);

  const connect = () => setChange({ change: { addons: [{ code: feature, action: "enable", quantity: 1 }] }, title: t("billing.enableAddon", { name }) });
  const cta = (big: boolean) => {
    if (purchasable && manage) {
      return (
        <Button size={big ? "lg" : "md"} className="promo-cta" leftIcon={<Sparkles />} onClick={connect}>
          {price !== undefined ? t("promo.connectFor", { price: formatMoney(price, catalog.data?.currency) }) : t("promo.connect")}
        </Button>
      );
    }
    if (manage) {
      return <Button size={big ? "lg" : "md"} className="promo-cta" rightIcon={<ArrowRight />} asChild><Link to="/settings/subscription">{plans.length ? t("promo.changePlan") : t("promo.openSubscription")}</Link></Button>;
    }
    return <p className="text-sm opacity-80">{t("promo.askOwner")}</p>;
  };
  const delay = (ms: number) => ({ "--d": `${ms}ms` }) as CSSProperties;

  return (
    <div className="flex flex-col gap-6">
      <section className="promo-hero rounded-card bg-rail px-5 py-10 text-white sm:px-10 md:py-14">
        <span className="promo-hero__blob promo-hero__blob--a" aria-hidden />
        <span className="promo-hero__blob promo-hero__blob--b" aria-hidden />
        <span className="promo-hero__grid" aria-hidden />
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.4fr_1fr]">
          <div className="flex flex-col items-start gap-5">
            <span className="promo-in inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold tracking-wide uppercase backdrop-blur" style={delay(0)}>
              <Lock className="size-3.5" aria-hidden />{t("promo.badge")}
            </span>
            <h1 className="promo-in text-3xl leading-tight font-extrabold tracking-tight sm:text-4xl lg:text-5xl" style={delay(80)}>{title}</h1>
            <p className="promo-in max-w-xl text-base text-white/75 sm:text-lg" style={delay(160)}>{tagline}</p>
            {plans.length > 0 && !purchasable && <p className="promo-in text-sm text-white/70" style={delay(200)}>{t("promo.inPlans", { plans: plans.join(", ") })}</p>}
            <div className="promo-in flex flex-wrap items-center gap-3" style={delay(240)}>
              {cta(true)}
              {manage && <Button size="lg" variant="ghost" className="text-white hover:bg-white/10" asChild><Link to="/settings/subscription">{t("promo.allAddons")}</Link></Button>}
            </div>
            <p className="promo-in text-xs text-white/55" style={delay(300)}>{t("promo.fineprint")}</p>
          </div>
          <div className="hidden justify-center lg:flex" aria-hidden>
            <div className="promo-orb">
              <span className="promo-orb__glow" />
              <span className="promo-orb__ring" />
              <span className="promo-orb__core"><Icon className="size-14" strokeWidth={1.6} /></span>
              {chips.map((k, i) => (
                <span key={k} className={`promo-chip promo-chip--${i + 1} rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-medium backdrop-blur`}>
                  <Check className="me-1 inline size-3.5 text-primary" />{tx(`${k}.t`)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {promo && (
        <>
          <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {BENEFITS.filter((b) => has(`${b}.t`)).map((b, i) => {
              const BIcon = BENEFIT_ICONS[i] ?? Sparkles;
              return (
                <article key={b} className="promo-card promo-in flex flex-col gap-3 rounded-card border border-border bg-surface p-5" style={delay(100 + i * 90)}>
                  <span className="promo-card__icon grid size-11 place-items-center rounded-tile text-white shadow-lg"><BIcon className="size-5" aria-hidden /></span>
                  <h2 className="text-base font-semibold">{tx(`${b}.t`)}</h2>
                  <p className="text-sm leading-relaxed text-muted">{tx(`${b}.d`)}</p>
                </article>
              );
            })}
          </section>
          <section className="promo-in grid grid-cols-1 gap-6 rounded-card border border-border bg-surface p-5 sm:p-8 lg:grid-cols-[1fr_1.6fr]" style={delay(300)}>
            <div className="flex flex-col gap-3">
              <Badge tone="info" className="self-start">{name}</Badge>
              <h2 className="text-2xl font-bold tracking-tight">{t("promo.insideTitle")}</h2>
              {has("who") && <p className="text-muted">{tx("who")}</p>}
            </div>
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {INSIDE.filter((k) => has(k)).map((k) => (
                <li key={k} className="flex gap-3 rounded-tile bg-surface-2 p-3">
                  <span className="grid size-6 shrink-0 place-items-center rounded-full bg-success-soft text-success"><Check className="size-4" aria-hidden /></span>
                  <span className="text-sm">{tx(k)}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      <section className="promo-band promo-in flex flex-col items-start gap-4 rounded-card p-6 text-white sm:flex-row sm:items-center sm:justify-between sm:p-8" style={delay(380)}>
        <div>
          <p className="text-xl font-bold">{t("promo.bandTitle")}</p>
          <p className="text-white/85">{t("promo.bandText", { name })}</p>
        </div>
        <div className="[&_.promo-cta]:bg-white [&_.promo-cta]:text-fg [&_.promo-cta:hover]:bg-white/90">{cta(false)}</div>
      </section>
      {change && <ChangeModal change={change.change} title={change.title} onClose={() => setChange(null)} />}
    </div>
  );
}
